import * as amplitude from '@amplitude/analytics-browser'
import { createContext, useCallback, useContext, useEffect, useMemo } from 'react'
import { isDevelopment } from '@/lib/general-helpers.ts'
import { CookieConsentCategoryEnum, waitForCookieConsent } from './cookie-consent.ts'
import { getAnonymousId, getCookieDomain } from './lok-aid.ts'

type InitializationSettings = {
	amplitudeApiKey?: string
}

export type TrackOptions = {
	eventType: string
	// biome-ignore lint/suspicious/noExplicitAny: Amplitude expects this type
	eventProperties?: Record<string, any>
}

export type Analytics = {
	track: <EventOptions extends TrackOptions>(options: EventOptions) => void
}

const camelToSnakeCase = (str: string): string =>
	str.length === 1 ? str.toLowerCase() : str.replace(/([A-Z])/g, '_$1').toLowerCase()

/**
 * Formats event names with consistent "Website - " prefix for all website events.
 * This ensures proper event organization and filtering in Amplitude.
 *
 * @example
 * formatEventName('Link Clicked') // Returns: 'Website - Link Clicked'
 * formatEventName('Pricing Plan Tab Clicked') // Returns: 'Website - Pricing Plan Tab Clicked'
 */
export const formatEventName = (eventName: string): string => {
	return `Website - ${eventName}`
}

/**
 * Creates a tracking context object with snake_case keys.
 * Use this to ensure consistent property naming in analytics events.
 * CamelCase keys are automatically converted to snake_case.
 *
 * @example
 * // In PlanCallToAction component
 * <AppLink
 *   href="/signup"
 *   trackingContext={createTrackingContext({
 *     plan: planTitle,           // 'Enterprise'
 *     planSection: 'overview',   // camelCase → plan_section
 *   })}
 * >
 *   Get Started
 * </AppLink>
 * // Results in event properties: { plan: 'Enterprise', plan_section: 'overview' }
 */
export function createTrackingContext<
	T extends Record<string, string | number | boolean | undefined>,
>(context: T): Record<string, string | number | boolean> {
	return Object.entries(context).reduce(
		(acc, [key, value]) => {
			if (value !== undefined) {
				acc[camelToSnakeCase(key)] = value
			}
			return acc
		},
		{} as Record<string, string | number | boolean>,
	)
}

export const AnalyticsContext = createContext<Analytics | null>(null)

export const useAnalytics = (): Analytics => {
	const analyticsContext = useContext(AnalyticsContext)
	if (!analyticsContext) throw new Error('useAnalytics must be called within an AnalyticsProvider')
	return analyticsContext
}

export const AnalyticsProvider = AnalyticsContext.Provider

export const useInitializeAnalytics = ({ amplitudeApiKey }: InitializationSettings): Analytics => {
	const isAmplitudeEnabled = !!amplitudeApiKey

	// Initialize Amplitude when enabled
	useEffect(() => {
		let cleanup: (() => void) | undefined
		let flush: (() => void) | undefined
		let onVisibilityChange: (() => void) | undefined

		if (isAmplitudeEnabled) {
			cleanup = waitForCookieConsent({
				categories: [CookieConsentCategoryEnum.Performance],
				onConsentProvided: () => {
					const domain = getCookieDomain()
					amplitude.init(amplitudeApiKey, {
						serverZone: 'EU',
						deviceId: getAnonymousId(),
						cookieOptions: {
							domain,
							expiration: 365,
							sameSite: 'Lax' as const,
							secure: !isDevelopment(),
						},
						autocapture: {
							attribution: true,
							pageViews: false, // GTM - Amplitude Analytics Browser SDK is configured to use the legacy page view properties
							sessions: true,
							formInteractions: false,
							fileDownloads: false,
							elementInteractions: false,
						},
					})

					// Switch to beacon transport on page exit to ensure events are sent
					// even when navigating to external URLs (e.g. subdomains)
					flush = () => {
						amplitude.setTransport('beacon')
						// Sets https transport to use `sendBeacon` API
						amplitude.flush()
					}

					// visibilitychange is the most reliable signal per MDN, especially on mobile - see: https://developer.mozilla.org/en-US/docs/Web/API/Navigator/sendBeacon
					onVisibilityChange = () => {
						if (document.visibilityState === 'hidden') flush?.()
					}
					document.addEventListener('visibilitychange', onVisibilityChange)
					// pagehide as fallback - as per amplitude docs: https://amplitude.com/docs/sdks/analytics/browser/browser-sdk-2#set-the-transport-to-use-sendbeacon-for-all-events
					window.addEventListener('pagehide', flush)
				},
			})
		}

		return () => {
			cleanup?.()
			if (onVisibilityChange) document.removeEventListener('visibilitychange', onVisibilityChange)
			if (flush) window.removeEventListener('pagehide', flush)
		}
	}, [isAmplitudeEnabled, amplitudeApiKey])

	const track = useCallback(
		<EventOptions extends TrackOptions>(options: EventOptions) => {
			if (isAmplitudeEnabled) {
				try {
					// Our Amplitude configuration always expects snake_case event properties
					// We allow passing camelCase event properties, but we convert them to snake_case here
					const snakeCasedEventProperties = options.eventProperties
						? Object.fromEntries(
								Object.entries(options.eventProperties).map(([key, value]) => [
									camelToSnakeCase(key),
									value,
								]),
							)
						: undefined

					/**
					 * @see https://www.docs.developers.amplitude.com/data/sdks/browser-2/#tracking-an-event
					 */
					amplitude.track(options.eventType, snakeCasedEventProperties)
				} catch (error) {
					if (isDevelopment()) {
						// biome-ignore lint/suspicious/noConsole: Log analytics failures for debugging
						console.error('Analytics tracking failed:', error, options)
					}
				}
			}
		},
		[isAmplitudeEnabled],
	)

	return useMemo(() => ({ track }), [track])
}
